import { useUserId, useUserInfo, useUserTeamId } from '@/hook/use-account'
import type { LinkProps } from '@/lib/link'
import { formatDate } from '@/lib/utils'
import {
  getFabricConversationFabricList,
  getFabricConversationUserList,
  postFabricConversationUserInfo,
  postFabricMessagePageList,
  postFabricMessageRead,
  type FabricConversationResponse,
  type FabricMessageResponse,
  type FabricInfoListResponse,
  type ProductInfo,
  postFabricMessageReadConversation,
  type FabricProductAssociation,
} from '@/service/fabric-api'
import { create } from 'zustand'

type ChatUserInfo = ReturnType<typeof useUserInfo>

export enum InquirySourceType {
  Goods = 1,
  Fabric = 2,
  Marchant = 3,
  Smart = 4,
}

export enum ChatFromType {
  Goods = 1,
  Fabric = 2,
  Merchant = 3,
  Smart = 4,
}

export enum LongLinkMessageTypes {
  // 未读
  NOREAD = 1,
  // 对方已读
  READ = 2,
  // 对方提交消息
  SUBMIT = 3,
  // 已读会话
  CONVERSATION = 4,
}

export enum MessageTypes {
  // 时间
  TIME = 0,
  // 文本
  TEXT = 1,
  // 图片
  IMAGE = 2,
  // 面料
  FABRIC = 3,
  // 商品
  PRODUCT = 4,
  // 面料案例
  FABRIC_CASE = 5,
  // 联系方式
  CONTACT = 6,
  // // 系统消息
  // SYSTEM = 7,
  // 找布需求
  FIND_DEMAND = 7,
  // 找布需求分配结果
  FIND_DEMAND_ASSIGNMENT = 8,
}

/** 测试代码 ： 找布需求卡片消息的兼容编码前缀：用于后端暂不支持 FIND_DEMAND 类型时，仍可通过 TEXT 发送并在前端解码展示 */
export const FIND_DEMAND_TEXT_PREFIX = '__FIND_DEMAND__:' as const

interface BaseMessage extends FabricMessageResponse {
  isMe: boolean
  type: MessageTypes
}

export interface TimeMessage extends BaseMessage {
  type: MessageTypes.TIME
}

export interface TextMessage extends BaseMessage {
  type: MessageTypes.TEXT
}

export interface ImageMessage extends BaseMessage {
  type: MessageTypes.IMAGE
}

export interface FabricMessage extends BaseMessage {
  type: MessageTypes.FABRIC
  message?: string
  data: {
    fabricId: number
    fabricDetailPic: string
    fabricName: string
    teamId: number
    teamName: string
  }
}

export interface ProductMessage extends BaseMessage {
  type: MessageTypes.PRODUCT
  message?: string
  data: ProductInfo & LinkProps
}

export interface FabricCaseMessage extends BaseMessage {
  type: MessageTypes.FABRIC_CASE
  data: {
    fabricId: string
    fabricName: string
    fabricCasePic: string
  }
}

export interface ContactMessage extends BaseMessage {
  type: MessageTypes.CONTACT
  isFill: boolean
  data: {
    phone?: string
    wechat?: string
    qrcode?: string
  }
}

function getContactShotMessage(content?: string | null) {
  try {
    const parsedContent = content ? JSON.parse(content) : {}
    return parsedContent.isFill ? '[客户已提交联系方式]' : '[邀请留下联系方式]'
  } catch {
    return '[邀请留下联系方式]'
  }
}

export interface FindDemandAssignmentMessage extends BaseMessage {
  type: MessageTypes.FIND_DEMAND_ASSIGNMENT
  /**系统提示 */
  data: {
    message?: string
    data: {
      /**id */
      assignFabricId: string[]
      assignFabricList: {
        category: string
        detailUrls: string[]
        fabric: string
        fabricId: string
        fabricName: string
        garmentDisplayPicList: string[]
        mainUrl: string
        price: string
        priceMax: string
        priceMin: string
        priceUnit: string
        sellerId: string
      }[]
      /** 分配上传的面料图片 */
      assignFabricPicList: string[]
      /**补充说明 */
      assignRemark: string
      /** 分配商家ID列表 */
      assignTeamId: string[]
      /** 分配商家名称列表 */
      assignTeamList: {
        contactPerson: string
        contactPhone: string
        sellerLogo: string
        sellerName: string
        primaryProducts: string
      }[]
      assignTeamName: string[]
      createdAt: string
      phone: string
      referenceDesc: string
      referencePicList: string[]
      requirementId: string
      sourceType: number
      status: number
      teamId: number
      teamName: string
      userId: number
      userName: string
      wechat: string
    }
  }
}

export enum FindDemandStatus {
  SERVING = 'SERVING',
  PENDING_CONFIRM = 'PENDING_CONFIRM',
  FINISHED = 'FINISHED',
}

export enum FindDemandSubmitChannel {
  MINI_PROGRAM = 'MINI_PROGRAM',
  WEB = 'WEB',
  OFFICIAL = 'OFFICIAL',
}

export interface FindDemand {
  id: string
  createdAt: number
  status: FindDemandStatus
  submitChannel: FindDemandSubmitChannel
  desc: string
  images: string[]
  quantity?: string
  expectedPrice?: string
  spotRequirement?: string
  phone?: string
  wechat?: string
}

export interface FindDemandMessage extends BaseMessage {
  type: MessageTypes.FIND_DEMAND
  data: {
    demand: FindDemand
  }
}

export type Message =
  | TimeMessage
  | TextMessage
  | ImageMessage
  | FabricMessage
  | ProductMessage
  | FabricCaseMessage
  | ContactMessage
  | FindDemandAssignmentMessage
  | FindDemandMessage

function isOutgoingMessage(item: FabricMessageResponse, userId: number, userTeamId: number, type: 'marchant' | 'user') {
  return item.fromUserId === userId || (type === 'marchant' && item.fromTeamId === userTeamId && !item.isSystem)
}

function shouldHideSystemMessage(item: Pick<FabricMessageResponse, 'isSystem'>, type: 'marchant' | 'user') {
  return type === 'user' && !!item.isSystem
}

function applyContactSubmittedStatus(messages: Message[], messageIds?: number[]) {
  if (!messageIds?.length) return messages
  const messageIdSet = new Set(messageIds)
  return messages.map((item) => (item.id !== undefined && messageIdSet.has(item.id) ? { ...item, contactSubmitted: true } : item))
}

interface ChatState {
  userInfo: ChatUserInfo
  userId: number
  getUserInfo: () => void
  userTeamId: number
  getUserTeamId: () => void
  callRule: boolean
  setCallRule: (value: boolean) => void
  superAdmin: boolean
  setSuperAdmin: (value: boolean) => void
  dnd: number
  setDnd: (value: number) => void
  type: 'marchant' | 'user'
  setType: (type: 'marchant' | 'user') => void
  loading: boolean
  noReadTotal: number
  setNoReadTotal: (num: number) => void
  pageNo: number
  pageSize: number
  total: number
  messages: Message[]
  getMessage: (conversationId: string) => void
  reloadMessage: (conversationId: string) => void
  nextMessage: () => void
  updateMessage: (messageId: number[], data: object) => void
  sendMessage: (conversationId: string, message: FabricMessageResponse) => void
  acceptMessage: (message: FabricMessageResponse, isAtBottom?: boolean) => void
  readMessage: (messageIds: number[]) => void
  readAllUnreadMessage: () => void
  readMyAllMessage: () => void
  filterMessage: (item: FabricMessageResponse) => Message
  getShotMessage: (item: FabricMessageResponse) => string
  updateUnread: () => void
  merchantView: boolean
  setMerchantView: (value?: boolean) => void
  recentlyView: boolean
  setRecentlyView: (value?: boolean) => void
  chats: (FabricConversationResponse & { showLatestMessage: string })[]
  getChats: () => void
  getUserChats: () => void
  targetChatUserId: number | null
  setTargetChatUserId: (userId?: number) => void
  targetChatFabricTeamId: number | null
  setTargetChatFabricTeamId: (fabricTeamId?: number) => void
  defaultMessage: string
  setDefaultMessage: (value: string) => void
  currentChat: FabricConversationResponse | null
  setCurrentChat: (chat: FabricConversationResponse | null, autoRead?: boolean) => void
  readConversation: (conversationId: string, fromTeamId: number, fromUserId: number) => void
  getCustomerInfo: (conversationId: string) => void
  customerInfo: FabricConversationResponse
  setCustomerInfo: (info: FabricConversationResponse) => void
  guessFabric: FabricInfoListResponse | null
  setGuessFabric: (goods: FabricInfoListResponse | null) => void
  guessGoods: (Omit<FabricProductAssociation, 'sourceChannel'> & { sourceChannel?: number; submitPageUrl?: string }) | null
  setGuessGoods: (
    goods: (Omit<FabricProductAssociation, 'sourceChannel'> & { sourceChannel?: number; submitPageUrl?: string }) | null,
  ) => void
  isAtBottom: boolean
  setIsAtBottom: (value: boolean) => void
  isIframeVisible: boolean
  setIsIframeVisible: (value: boolean) => void
}

export const useChatStore = create<ChatState>((set, get) => ({
  // 当前登录用户的基本信息
  userInfo: {} as ChatUserInfo,
  userId: 0,
  // 获取并保存当前登录用户的基本信息
  getUserInfo: () => {
    set(() => ({
      userInfo: useUserInfo(),
      userId: useUserId(),
    }))
  },
  userTeamId: 0,
  getUserTeamId: () => {
    set(() => ({
      userTeamId: useUserTeamId(),
    }))
  },
  // 是否有打招呼权限
  callRule: false,
  // 设置是否有打招呼权限
  setCallRule: (value: boolean) => {
    set(() => ({ callRule: value }))
  },
  // 是否超级管理员
  superAdmin: false,
  // 设置是否为超级管理员
  setSuperAdmin(value: boolean) {
    set(() => ({ superAdmin: value }))
  },
  // 是否免打扰 0:否 1:是
  dnd: 0,
  // 设置是否免打扰
  setDnd: (value: number) => {
    set(() => ({ dnd: value }))
  },
  // 商家端：marchant 用户端：user
  type: 'marchant' as const,
  // 设置当前为商家端还是用户端
  setType: (type: 'marchant' | 'user') => {
    set(() => ({ type }))
  },
  // 用户端是否打开商家详情
  merchantView: true,
  // 设置用户端是否打开商家详情
  setMerchantView: (value?: boolean) => {
    set((state) => ({ merchantView: value === undefined ? !state.merchantView : value }))
  },
  // 用户端是否打开最近查看
  recentlyView: false,
  // 用户端控制是否打开最近查看
  setRecentlyView: (value?: boolean) => {
    set((state) => ({ recentlyView: value === undefined ? !state.recentlyView : value }))
  },
  // 控制是否正在获取消息数据
  loading: false,
  // 当前总共未读消息数量
  noReadTotal: 0,
  // 设置当前总共未读消息数量
  setNoReadTotal: (num: number) => {
    set(() => ({
      noReadTotal: num,
    }))
  },
  // 消息列表页码
  pageNo: 1,
  // 消息列表每页数量
  pageSize: 10,
  // 当前会话总共的消息数量
  total: 0,
  // 消息列表
  messages: [],
  // 获取消息列表
  getMessage: (conversationId: string) => {
    set(() => ({ loading: true }))
    postFabricMessagePageList({
      conversationId,
      pageNo: get().pageNo,
      pageSize: get().pageSize,
    })
      .then((res) => {
        if (conversationId !== get().currentChat?.conversationId) return
        set((state) => {
          // 用户端不展示系统消息
          const newRes = (res.resultList || []).filter((item) => !shouldHideSystemMessage(item, state.type))
          // 根据消息id进行去重
          const existingIds = new Set((state.messages || []).map((m) => m.id))
          // 对不同类型的消息进行格式化
          const incoming = newRes.map((item) => state.filterMessage(item))
          // 保留去重后的消息
          const dedupedIncoming = incoming.filter((m) => !existingIds.has(m.id))
          return {
            total: res.resultCount,
            messages: [...state.messages, ...dedupedIncoming],
          }
        })
      })
      .finally(() => {
        set(() => ({ loading: false }))
      })
  },
  reloadMessage: (conversationId: string) => {
    set(() => ({ messages: [], pageNo: 1, total: 0, loading: true }))
    postFabricMessagePageList({
      conversationId,
      pageNo: get().pageNo,
      pageSize: get().pageSize,
    })
      .then((res) => {
        if (conversationId !== get().currentChat?.conversationId) return
        set((state) => {
          // 用户端不展示系统消息
          const newRes = (res.resultList || []).filter((item) => !shouldHideSystemMessage(item, state.type))
          // 根据消息id进行去重
          const existingIds = new Set((state.messages || []).map((m) => m.id))
          // 对不同类型的消息进行格式化
          const incoming = newRes.map((item) => state.filterMessage(item))
          // 保留去重后的消息
          const dedupedIncoming = incoming.filter((m) => !existingIds.has(m.id))
          return {
            total: res.resultCount,
            messages: [...state.messages, ...dedupedIncoming],
          }
        })
      })
      .finally(() => {
        set(() => ({ loading: false }))
      })
  },
  // 获取下一页消息
  nextMessage: () => {
    set((state) => ({ pageNo: state.pageNo + 1 }))
    get().getMessage(get().currentChat?.conversationId || '')
  },
  // 根据消息id更新消息数据
  updateMessage: (messageId: number[], data: object) => {
    set((state) => {
      return {
        messages: state.messages.map((item) => {
          if (messageId.includes(item.id!)) {
            return {
              ...item,
              ...data,
            }
          } else {
            return item
          }
        }),
      }
    })
    get().updateUnread()
  },
  // 发送消息
  sendMessage: (conversationId: string, message: FabricMessageResponse) => {
    // 如果发送的消息目标不是当前会话，则直接返回，防止发送过程中切换会话导致消息展示错误
    if (conversationId !== get().currentChat?.conversationId) return
    set((state) => ({
      // 将消息加入消息列表，展示是倒叙，所以要加在最前面
      messages: [
        state.filterMessage(message),
        ...applyContactSubmittedStatus(state.messages, message.contactSubmittedMessageIds),
      ],
      // 根据最新的消息更新会话列表的最新消息状态和时间，并根据最新消息时间进行倒序排序
      chats: state.chats
        .map((item) => {
          if (item.conversationId === state.currentChat?.conversationId) {
            return {
              ...item,
              showLatestMessage: state.getShotMessage(message),
              lastMessageTime: formatDate(new Date(message.sendTime!), 'YYYY-MM-DD HH:mm:ss'),
            }
          } else {
            return item
          }
        })
        .sort((a, b) => {
          if (a.lastMessageTime! > b.lastMessageTime!) {
            return -1
          } else {
            return 1
          }
        }),
    }))
  },
  acceptMessage: (message: FabricMessageResponse, isAtBottom?: boolean) => {
    // console.log('接收到消息：', message)
    if (shouldHideSystemMessage(message, get().type)) {
      return
    }
    const filteredMessage = get().filterMessage(message)
    const applySubmittedStatus = (messages: Message[]) =>
      applyContactSubmittedStatus(messages, message.contactSubmittedMessageIds)
    const isOutgoing = filteredMessage.isMe
    // 会话不可见的时候
    if (!get().isIframeVisible) {
      set((state) => {
        const updateMessage =
          message.conversationId === state.currentChat?.conversationId
            ? [filteredMessage, ...applySubmittedStatus(state.messages)]
            : state.messages
        const updatedChats = state.chats.map((item) => {
          if (item.conversationId === message.conversationId) {
            return {
              ...item,
              showLatestMessage: get().getShotMessage(message),
              lastMessageTime: formatDate(new Date(message.sendTime || 0), 'YYYY-MM-DD HH:mm:ss'),
              countMessage: (item.countMessage || 0) + (isOutgoing ? 0 : 1),
            }
          } else {
            return item
          }
        })
        return {
          messages: updateMessage,
          chats: updatedChats,
          noReadTotal: updatedChats.reduce((a, b) => a + (b.countMessage || 0), 0),
        }
      })
      return
    }
    // 非当前聊天的用户的时候
    if (message.conversationId !== get().currentChat?.conversationId) {
      // console.log('非当前聊天用户')
      // 更新对应会话的最新消息及会话顺序，同步更新总未读数量
      set((state) => {
        const updatedChats = state.chats.map((item) => {
          if (item.conversationId === message.conversationId) {
            return {
              ...item,
              showLatestMessage: get().getShotMessage(message),
              lastMessageTime: formatDate(new Date(message.sendTime || 0), 'YYYY-MM-DD HH:mm:ss'),
              countMessage: (item.countMessage || 0) + (isOutgoing ? 0 : 1),
            }
          } else {
            return item
          }
        })
        return {
          chats: updatedChats,
          noReadTotal: updatedChats.reduce((a, b) => a + (b.countMessage || 0), 0),
        }
      })
      get().updateUnread()
      // console.log(
      //   '接收消息后未读数量：',
      //   get().noReadTotal,
      //   get().chats.filter((item) => item.countMessage),
      // )
      return
    }
    // console.log('当前聊天用户')
    // 当前聊天的用户
    // 如果是系统消息则重新获取用户信息，更新右侧展示内容
    if (message.isSystem) {
      get().getCustomerInfo(message.conversationId!)
    }

    if (isAtBottom) {
      // 仅对对方消息立即标记已读；托管消息在知小布里按我方消息展示。
      if (!isOutgoing) {
        postFabricMessageRead([filteredMessage.id])
      }
      set((state) => {
        // 判断是否重复消息，若已存在则不加入消息列表
        const exists = state.messages.some((m) => m.id === filteredMessage.id)
        return {
          messages: exists
            ? applySubmittedStatus(state.messages)
            : [{ ...filteredMessage, isRead: isOutgoing ? filteredMessage.isRead : 1 }, ...applySubmittedStatus(state.messages)],
        }
      })
    } else {
      // 如果不在底部，将消息直接加入列表，保持未读状态
      set((state) => {
        const exists = state.messages.some((m) => m.id === filteredMessage.id)
        return {
          messages: exists ? applySubmittedStatus(state.messages) : [filteredMessage, ...applySubmittedStatus(state.messages)],
        }
      })
      // 更新相关状态
      if (!isOutgoing) {
        get().updateUnread()
      }
    }
    // console.log(
    //   '接收消息后未读数量：',
    //   get().noReadTotal,
    //   get().chats.filter((item) => item.countMessage),
    // )
  },
  // 已读消息
  readMessage: (messageIds: number[]) => {
    // 找到存在于会话列表中并且未读的消息id
    const readItems = messageIds.filter((item) =>
      get().messages.find((message) => message.id === item && !message.isMe && !message.isRead),
    )
    // 存在未读消息，调用接口进行已读操作
    // console.log('已读消息：', readItems)
    if (readItems.length > 0) {
      postFabricMessageRead(readItems)
    }
    // 将消息设置为已读状态
    get().updateMessage(readItems, { isRead: 1 })
    // 更新相关数据
    get().updateUnread()
    // console.log(
    //   '已读完成后未读数量：',
    //   get().noReadTotal,
    //   get().chats.filter((item) => item.countMessage),
    // )
  },
  // 已读当前会话所有未读的消息
  readAllUnreadMessage: () => {
    const unreadMessageIds = get()
      .messages.filter((item) => !item.isMe && !item.isRead)
      .map((item) => item.id!)
    get().readMessage(unreadMessageIds)
  },
  // 已读当前会话所有我发送的未读消息
  readMyAllMessage: () => {
    const unreadMessageIds = get()
      .messages.filter((item) => item.isMe && !item.isRead)
      .map((item) => item.id!)
    get().updateMessage(unreadMessageIds, { isRead: 1 })
  },
  // 根据消息类型进行格式化
  filterMessage: (item: FabricMessageResponse) => {
    if (!get().userId) {
      get().getUserInfo()
    }
    const isMe = isOutgoingMessage(item, get().userId, get().userTeamId, get().type)
    // 兼容：后端暂不支持 FIND_DEMAND 类型时，用 TEXT + 前缀编码发送，前端在此处解码成 FIND_DEMAND 展示
    if (typeof item.content === 'string' && item.content.startsWith(FIND_DEMAND_TEXT_PREFIX)) {
      const parsedContent = JSON.parse(item.content.slice(FIND_DEMAND_TEXT_PREFIX.length))
      return {
        ...item,
        type: MessageTypes.FIND_DEMAND,
        data: parsedContent,
        isMe: item.fromUserId === get().userId,
      } as FindDemandMessage
    }
    if (!item.type) {
      return {
        ...item,
        message: item.content!,
        isMe,
      } as Message
    }
    if (item.type === MessageTypes.FABRIC) {
      const parsedContent = JSON.parse(item.content!)
      return {
        ...item,
        type: item.type,
        data: parsedContent.data || {},
        message: parsedContent.message || '',
        isMe,
      } as FabricMessage
    } else if (item.type === MessageTypes.PRODUCT) {
      const parsedContent = JSON.parse(item.content!)
      return {
        ...item,
        type: item.type,
        message: parsedContent.message || '',
        data: parsedContent.data || {},
        isMe,
      } as ProductMessage
    } else if (item.type === MessageTypes.FABRIC_CASE) {
      const parsedContent = JSON.parse(item.content!)
      return {
        ...item,
        type: item.type,
        data: parsedContent.data || {},
        isMe,
      } as FabricCaseMessage
    } else if (item.type === MessageTypes.CONTACT) {
      const parsedContent = JSON.parse(item.content!)
      return {
        ...item,
        type: item.type,
        isFill: parsedContent.isFill,
        data: parsedContent.data || {},
        isMe,
      } as ContactMessage
    } else if (item.type === MessageTypes.FIND_DEMAND_ASSIGNMENT) {
      const parsedContent = item.content ? JSON.parse(item.content) : {}
      return {
        ...item,
        type: item.type,
        data: parsedContent,
        isMe,
      } as FindDemandAssignmentMessage
    } else if (item.type === MessageTypes.FIND_DEMAND) {
      const parsedContent = item.content ? JSON.parse(item.content) : {}
      return {
        ...item,
        type: item.type,
        data: parsedContent,
        isMe,
      } as FindDemandMessage
    }
    return {
      ...item,
      type: item.type,
      message: item.content!,
      isMe,
    } as TextMessage | ImageMessage
  },
  // 根据消息类型返回对应的缩写
  getShotMessage: (item: FabricMessageResponse) => {
    if (shouldHideSystemMessage(item, get().type)) {
      return ''
    }
    if (item.type === MessageTypes.TEXT && item.content?.startsWith(FIND_DEMAND_TEXT_PREFIX)) {
      return '[找布需求]'
    }
    if (item.type === MessageTypes.IMAGE) {
      return '[图片]'
    } else if (item.type === MessageTypes.FABRIC) {
      return '[面料]'
    } else if (item.type === MessageTypes.PRODUCT) {
      return '[商品]'
    } else if (item.type === MessageTypes.FABRIC_CASE) {
      return '[面料案例]'
    } else if (item.type === MessageTypes.CONTACT) {
      return getContactShotMessage(item.content)
    } else if (item.type === MessageTypes.FIND_DEMAND_ASSIGNMENT) {
      return '[找布需求推荐]'
    } else if (item.type === MessageTypes.FIND_DEMAND) {
      return '[找布需求]'
    }
    return item.content || ''
  },
  // 更新消息列表、会话列表、总未读数量
  updateUnread() {
    // console.log('更新未读信息')
    set((state) => {
      // 获取当前会话的未读数量
      const currentUnread = state.messages.filter(
        (item) => !item.isRead && !item.isMe && item.conversationId === state.currentChat?.conversationId,
      )
      // 更新会话列表的未读数量
      const currentChats = state.chats.map((item) => {
        if (item.conversationId === state.currentChat?.conversationId) {
          return {
            ...item,
            countMessage: currentUnread.length,
          }
        } else {
          return item
        }
      })
      // 更新当前会话的未读数量
      const currentChat = state.currentChat
        ? {
            ...state.currentChat,
            countMessage: currentUnread.length,
          }
        : null
      // 统计总共的未读数量
      const noReadTotal = currentChats.reduce((a, b) => a + (b.countMessage || 0), 0)
      return {
        messages: state.messages,
        chats: currentChats,
        currentChat,
        noReadTotal,
      }
    })
  },
  chats: [],
  // 获取商家端会话列表
  getChats: async () => {
    // console.log('获取商家端会话列表')
    const res = await getFabricConversationFabricList()
    // 更新会话列表和总未读数量
    set(() => ({
      chats: res.map((item) => {
        return {
          ...item,
          showLatestMessage: get().getShotMessage(item),
        }
      }),
      noReadTotal: res.reduce((a, b) => {
        return a + (b.countMessage || 0)
      }, 0),
    }))
    if (!res.length || (get().currentChat && !get().targetChatFabricTeamId && !get().targetChatUserId)) return
    // 如果不存在当前联系人，则进行更新，先判断是否有目标联系人，若没有则默认第一个
    let defaultChat = res[0]
    let flag = false
    if (get().targetChatUserId) {
      defaultChat = res.find((item) => item.userId === get().targetChatUserId) || res[0]
      flag = true
      set(() => ({ targetChatUserId: null }))
    }
    if (get().targetChatFabricTeamId) {
      defaultChat = res.find((item) => item.fabricTeamId === get().targetChatFabricTeamId) || res[0]
      flag = true
      set(() => ({ targetChatFabricTeamId: null }))
    }
    // 自动选择默认聊天时不自动标记为已读
    get().setCurrentChat(defaultChat, flag)
  },
  // 获取用户端会话列表
  getUserChats: async () => {
    // console.log('获取用户端会话列表')
    const res = await getFabricConversationUserList()
    set(() => ({
      chats: res
        .sort((a, b) => (b.isCustomer || 0) - (a.isCustomer || 0))
        .map((item) => {
          return {
            ...item,
            showLatestMessage: get().getShotMessage(item),
          }
        }),
    }))
    const isInChats = res.find((item) => item.conversationId === get().currentChat?.conversationId)
    if (!res.length || (get().currentChat && isInChats && !get().targetChatFabricTeamId && !get().targetChatUserId)) return
    // 如果不存在当前联系人，则进行更新，先判断是否有目标联系人，若没有则默认第一个
    let defaultChat = res[0]
    let flag = false
    if (get().targetChatUserId) {
      defaultChat = res.find((item) => item.userId === get().targetChatUserId) || res[0]
      flag = true
      set(() => ({ targetChatUserId: null }))
    }
    if (get().targetChatFabricTeamId) {
      defaultChat = res.find((item) => item.fabricTeamId === get().targetChatFabricTeamId) || res[0]
      flag = true
      set(() => ({ targetChatFabricTeamId: null }))
    }
    if (defaultChat) {
      // 自动选择默认聊天时不自动标记为已读
      get().setCurrentChat(defaultChat, flag)
    }
  },
  // 目标聊天用户
  targetChatUserId: null,
  setTargetChatUserId: (userId?: number) => {
    set(() => ({ targetChatUserId: userId }))
  },
  // 目标聊天商家
  targetChatFabricTeamId: null,
  setTargetChatFabricTeamId: (fabricTeamId?: number) => {
    set(() => ({ targetChatFabricTeamId: fabricTeamId }))
  },
  defaultMessage: '',
  setDefaultMessage: (value: string) => {
    set(() => ({ defaultMessage: value }))
  },
  // 当前联系人
  currentChat: null,
  setCurrentChat: (chat: FabricConversationResponse | null, autoRead?: boolean) => {
    if (!chat) {
      return set(() => ({ currentChat: null, customer: {}, messages: [], pageNo: 1, total: 0 }))
    }
    if (chat.conversationId === get().currentChat?.conversationId) return
    // console.log('选中会话：', chat, autoRead)
    set(() => ({ currentChat: chat, customer: {}, customerInfo: chat, messages: [], pageNo: 1, total: 0 }))
    get().getMessage(chat.conversationId!)
    get().getCustomerInfo(chat.conversationId!)
    // 只有在iframe可见时才自动标记为已读
    if (autoRead && (get().isIframeVisible || get().type === 'marchant')) {
      // console.log('自动已读会话')
      get().readConversation(
        chat.conversationId!,
        get().type === 'marchant' ? chat.teamId! : chat.fabricTeamId!,
        get().type === 'marchant' ? chat.userId! : chat.fabricUserId!,
      )
      // 更新本地状态，将当前会话的所有未读消息标记为已读
      set((state) => ({
        chats: state.chats.map((item) => {
          if (item.conversationId === chat.conversationId) {
            return {
              ...item,
              countMessage: 0,
            }
          } else {
            return item
          }
        }),
        noReadTotal: state.chats.reduce((a, b) => {
          return a + (b.conversationId === chat.conversationId ? 0 : b.countMessage || 0)
        }, 0),
      }))
      // console.log(
      //   '已读完成后未读数量：',
      //   get().noReadTotal,
      //   get().chats.filter((item) => item.countMessage),
      // )
    }
  },
  readConversation: (conversationId: string, fromTeamId: number, fromUserId: number) => {
    // console.log('已读当前会话框：', conversationId, fromTeamId)
    postFabricMessageReadConversation({
      fromTeamId: fromTeamId,
      fromUserId: fromUserId,
      conversationId: conversationId,
    })
  },
  // 获取当前联系人的信息
  getCustomerInfo: (conversationId: string) => {
    postFabricConversationUserInfo({
      conversationId: conversationId,
    }).then((res) => {
      if (conversationId !== get().currentChat?.conversationId) return
      set(() => ({ customerInfo: res }))
    })
  },
  // 当前联系人的信息
  customerInfo: {},
  setCustomerInfo: (info: FabricConversationResponse) => {
    set(() => ({ customerInfo: info }))
  },
  // 大知衣发起咨询猜测要发送的面料
  guessFabric: null,
  setGuessFabric: (goods: FabricInfoListResponse | null) => {
    set(() => ({ guessFabric: goods }))
  },
  // 大知衣发起咨询猜测要发送的商品
  guessGoods: null,
  setGuessGoods: (
    goods: (Omit<FabricProductAssociation, 'sourceChannel'> & { sourceChannel?: number; submitPageUrl?: string }) | null,
  ) => {
    set(() => ({ guessGoods: goods }))
  },
  // 滚动条是否在底部
  isAtBottom: true,
  setIsAtBottom: (value: boolean) => {
    // console.log('滚动条是否在底部：', value)
    set(() => ({ isAtBottom: value }))
  },
  isIframeVisible: false,
  setIsIframeVisible: (value: boolean) => {
    // console.log('用户端会话窗口是否打开：', value)
    set(() => ({ isIframeVisible: value }))
  },
}))
